<template>
  <form-wrapper>
    <b-form v-if="show" @submit="onSubmit">
      <h2 class="text-center">
        Add a new file
      </h2>

      <b-form-group>
        <b-form-select
          id="type-selection"
          :options="typeOptions"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('preference') }"
          v-model="typeSelection"
          name="preference"/>
        <validation-errors :validation-errors="[errors.first('preference')]"/>
      </b-form-group>

      <template v-if="typeSelection">

        <template v-if="typeSelection === 'image'">
          <image-upload
            @fileForEdit="selectFile"
            @cropImage="setCropImage"
            @name="setName"
            @imageUploadHasError="setImageUploadHasError"
          />
        </template>

        <template v-if="typeSelection === 'pdf'">
          <label for="file-input">PDF</label>
          <b-form-group>
            <input
              v-validate="'required|validateAnyFile|mimes:application/pdf'"
              id="file-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('file') || apiErrors.file.length }"
              type="file"
              accept="application/pdf"
              name="file"
              @change="setFile"/>
            <validation-errors :validation-errors="[errors.first('file')]"/>
            <validation-errors v-if="!errors.has('file')" :validation-errors="apiErrors.file"/>
          </b-form-group>
        </template>

        <template v-if="typeSelection === 'video'">
          <label for="file-input">Video</label>
          <b-form-group>
            <input
              v-validate="'required|validateAnyFile|mimes:video/mp4'"
              id="file-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('file') || apiErrors.file.length }"
              type="file"
              accept="video/mp4"
              name="file"
              @change="setFile"/>
            <validation-errors :validation-errors="[errors.first('file')]"/>
            <validation-errors v-if="!errors.has('file')" :validation-errors="apiErrors.file"/>
          </b-form-group>

          <label for="thumbnail-input">Video Thumbnail</label>
          <b-form-group>
            <input
              v-validate="'required|validateThumbnail'"
              id="thumbnail-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('thumbnail') || apiErrors.thumbnail.length }"
              type="file"
              accept="image/png,image/jpeg"
              name="thumbnail"
              @change="setThumbnail"/>
            <validation-errors :validation-errors="[errors.first('thumbnail')]"/>
            <validation-errors v-if="!errors.has('thumbnail')" :validation-errors="apiErrors.thumbnail"/>
          </b-form-group>
        </template>

        <template v-if="typeSelection === 'audio'">
          <label for="file-input">Audio</label>
          <b-form-group>
            <input
              v-validate="'required|validateAnyFile|mimes:audio/mpeg,audio/wav'"
              id="file-input"
              :disabled="saving"
              :class="{ 'is-invalid' : errors.has('file') || apiErrors.file.length }"
              type="file"
              accept="audio/mpeg,audio/wav"
              name="file"
              @change="setFile"/>
            <validation-errors :validation-errors="[errors.first('file')]"/>
            <validation-errors v-if="!errors.has('file')" :validation-errors="apiErrors.file"/>
          </b-form-group>
        </template>

        <b-form-group>
          <label for="name-input">Name</label>
          <b-form-input
            v-validate="'required'"
            id="name-input"
            :disabled="saving"
            :class="{ 'is-invalid' : errors.has('name') || apiErrors.name.length }"
            :value="fields.name"
            class="form-control"
            type="text"
            name="name"
            @input="setName"/>
          <validation-errors :validation-errors="[errors.first('name')]"/>
          <validation-errors v-if="!errors.has('name')" :validation-errors="apiErrors.name"/>
        </b-form-group>
	
        <relate-modules
          :initial-modules="[]"
          :disabled="saving"
          namespace="media"/>

        <b-form-group class="form-buttons">
          <b-button :disabled="disabled" type="submit" variant="primary">
            Confirm
            <span v-if="saving"><i class="fas fa-spinner fa-spin"></i></span>
          </b-button>
        </b-form-group>

        <uploading-video v-if="showVideoAlert"/>
        <form-success :success="success" class="form-alert">Media Created!</form-success>
      </template>

    </b-form>

  </form-wrapper>
</template>

<script>
import { capitalizeFirstLetter } from '../../../../helpers'
import { mapState, mapActions } from 'vuex'
import formSuccess from '../../../common/form/form-success'
import formWrapper from '../../../common/form/form-wrapper'
import relateModules from '../modules/relateModules'
import uploadingVideo from '../../../common/form/uploading-video'
import validationErrors from '../../../common/form/validation-errors'
import imageUpload from '../../../common/form/image-upload'

export default {
  components: {
    formSuccess,
    formWrapper,
    relateModules,
    uploadingVideo,
    validationErrors,
    imageUpload
  },
  data () {
    return {
      show: true,
      typeSelection: null,
      typeOptions: [
        { text: 'Media Type', value: null },
        { text: 'Image', value: 'image' },
        { text: 'Video', value: 'video' },
        { text: 'PDF', value: 'pdf' },
        { text: 'Audio', value: 'audio' }
      ],
      cropImage: false,
      imageUploadHasError : false
    }
  },
  computed: {
    ...mapState('media/forms/store', [
      'apiErrors',
      'fields',
      'success',
      'saving'
    ]),
    ...mapState('config', {
      apiConfig: 'data'
    }),
    disabled () {
      return this.errors.any() || this.saving || this.imageUploadHasError
    },
    typeDisplay () {
      return capitalizeFirstLetter(this.typeSelection)
    },
    showVideoAlert () {
      return this.typeSelection === 'video' && this.saving
    }
  },
  watch: {
    success (newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.errors.clear()
        this.$store.commit('media/forms/store/resetFields')
        this.show = false
        this.$nextTick(() => { this.show = true })
        this.uneditedFile = null
        this.previewImage = null
      }
    },
    typeSelection (newValue, oldValue) {
      this.reset()
    }
  },
  methods: {
    ...mapActions({
      storeMedia: 'media/store'
    }),
    onSubmit (event) {
      event.preventDefault()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.storeMedia()
        }
      })
    },
    setName (event) {
      this.$store.commit('media/forms/store/setField', {
        field: 'name',
        value: event
      })
    },
    setFile (event) {
      let file
      if (event.target && event.target.files) {
        file = event.target.files[0]
      } else {
        file = event
      }
      this.selectFile(file)
    },
    selectFile (file) {      
      let fileName = null
      let first = true
      if (this.fields.file) {
        fileName = this.fields.file.name
        first = false
      }

      this.$store.commit('media/forms/store/setField', {
        field: 'file',
        value: file
      })

      const updatedFileName = this.fields.file.name
      const changed = fileName !== updatedFileName

      if (
        this.fields.name === '' ||
        this.fields.name === null ||
        !first && changed
      ) {
        this.$store.commit('media/forms/store/setField', {
          field: 'name',
          value: updatedFileName.replace(/\.[^/.]+$/, '')
        })
      }

      if (this.typeSelection === 'image') {
               
        if (this.cropImage) {
          this.$store.commit('media/forms/store/setField', {
            field: 'crop_image',
            value: this.cropImage
          })
        }

      }
    },
    setThumbnail (event) {
      this.$store.commit('media/forms/store/setField', {
        field: 'thumbnail',
        value: event.target.files[0]
      })
    },
    setCropImage (value) {
      this.cropImage = value
    },
    reset () {
      this.errors.clear()
      this.$store.commit('media/forms/store/resetState')
      this.show = false
      this.$nextTick(() => { this.show = true })
    },
    setImageUploadHasError(hasError) {
      this.imageUploadHasError = hasError
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/admin/media/add.vue